/**
 * Frontend Subscription Service
 *
 * A comprehensive service for handling feature access, subscription management,
 * and plan-based permissions in the frontend application.
 *
 * Features:
 * - Feature access checking with caching
 * - Plan tier validation
 * - React hooks for easy component integration
 * - Fallback access configuration
 *
 * Usage Examples:
 *
 * // Service usage
 * import { frontendSubscriptionService } from '../base/services/frontendSubscriptionService';
 * const hasAccess = await frontendSubscriptionService.hasFeatureAccess('scopingAi');
 *
 * // React hook usage
 * import { useFeatureAccess } from '../base/services/frontendSubscriptionService';
 * const { hasAccess, isLoading, error } = useFeatureAccess('scopingAi');
 *
 * // Multiple features
 * import { useMultipleFeatureAccess } from '../base/services/frontendSubscriptionService';
 * const { accessMap, isLoading } = useMultipleFeatureAccess(['scopingAi', 'crm', 'pseo']);
 *
 * // User plan info
 * import { useUserPlan } from '../base/services/frontendSubscriptionService';
 * const { planName, isLoading } = useUserPlan();
 */

import { useState, useEffect } from "react";
import { supabase } from "../utils/supabaseClient";
import {
  PlanTier,
  SubscriptionRecord,
  SubscriptionStatus,
  ResourceUsage,
  SubscriptionSummary,
} from "../types/subscription";
import {
  getUserSubscription,
  getOrganizationSubscription,
  getSubscriptionSummary,
  getAvailablePlans,
} from "./subscriptionService";

// Feature access configuration
interface FeatureConfig {
  [featureName: string]: {
    requiredTierLevel?: number;
    requiredFeatures?: string[];
    fallbackAccess?: boolean;
  };
}

// Default feature configuration
const FEATURE_CONFIG: FeatureConfig = {
  scopingAi: {
    requiredTierLevel: 0, // Available from basic tier
    requiredFeatures: ["scopingai"],
    fallbackAccess: true, // Allow access if no subscription found
  },
  knowledge: {
    requiredTierLevel: 0,
    requiredFeatures: ["knowledgebase"],
    fallbackAccess: true,
  },
  crm: {
    requiredTierLevel: 0,
    requiredFeatures: ["crm"],
    fallbackAccess: true,
  },
  pseo: {
    requiredTierLevel: 1, // Pro tier and above
    requiredFeatures: ["pseo"],
    fallbackAccess: false,
  },
  aichat: {
    requiredTierLevel: 0,
    requiredFeatures: ["aichat"],
    fallbackAccess: true,
  },
  formengine: {
    requiredTierLevel: 2, // Enterprise tier only
    requiredFeatures: ["formengine", "admin"],
    fallbackAccess: false,
  },
};

class FrontendSubscriptionService {
  private subscriptionCache: Map<string, SubscriptionRecord | null> = new Map();
  private planCache: Map<string, PlanTier> = new Map();
  private cacheExpiry: Map<string, number> = new Map();
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  /**
   * Check if user has access to a specific feature
   */
  async hasFeatureAccess(
    featureName: string,
    userId?: string,
    organizationId?: string
  ): Promise<boolean> {
    try {
      console.log(`🔐 Checking feature access for: ${featureName}`);
      
      const featureConfig = FEATURE_CONFIG[featureName];
      if (!featureConfig) {
        console.warn(`⚠️ No configuration found for feature: ${featureName}`);
        return true; // Default to allow access for unknown features
      }

      // Get current user if not provided
      if (!userId) {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          console.log("❌ No authenticated user found");
          return featureConfig.fallbackAccess || false;
        }
        userId = user.id;
      }

      // Get user's subscription
      const subscription = await this.getUserSubscriptionCached(userId);
      
      if (!subscription) {
        console.log(`ℹ️ No subscription found for user, using fallback access: ${featureConfig.fallbackAccess}`);
        return featureConfig.fallbackAccess || false;
      }

      // Check subscription status
      if (subscription.status !== SubscriptionStatus.ACTIVE && 
          subscription.status !== SubscriptionStatus.TRIALING) {
        console.log(`❌ Subscription not active: ${subscription.status}`);
        return false;
      }

      // Get plan details
      const plan = await this.getPlanCached(subscription.plan_id);
      if (!plan) {
        console.log("❌ Plan not found");
        return featureConfig.fallbackAccess || false;
      }

      // Check tier level requirement
      if (featureConfig.requiredTierLevel !== undefined) {
        if (plan.tier_level < featureConfig.requiredTierLevel) {
          console.log(`❌ Tier level insufficient: ${plan.tier_level} < ${featureConfig.requiredTierLevel}`);
          return false;
        }
      }

      // Check required features in plan
      if (featureConfig.requiredFeatures) {
        const planFeatures = plan.features || {};
        for (const requiredFeature of featureConfig.requiredFeatures) {
          if (!this.hasFeatureInPlan(planFeatures, requiredFeature)) {
            console.log(`❌ Required feature not found in plan: ${requiredFeature}`);
            return false;
          }
        }
      }

      console.log(`✅ Feature access granted for: ${featureName}`);
      return true;

    } catch (error) {
      console.error(`Error checking feature access for ${featureName}:`, error);
      const featureConfig = FEATURE_CONFIG[featureName];
      return featureConfig?.fallbackAccess || false;
    }
  }

  /**
   * Get user's subscription with caching
   */
  private async getUserSubscriptionCached(userId: string): Promise<SubscriptionRecord | null> {
    const cacheKey = `user_${userId}`;
    const now = Date.now();
    
    // Check cache
    if (this.subscriptionCache.has(cacheKey)) {
      const expiry = this.cacheExpiry.get(cacheKey) || 0;
      if (now < expiry) {
        return this.subscriptionCache.get(cacheKey) || null;
      }
    }

    // Fetch from database
    try {
      const subscription = await getUserSubscription(userId);
      this.subscriptionCache.set(cacheKey, subscription);
      this.cacheExpiry.set(cacheKey, now + this.CACHE_DURATION);
      return subscription;
    } catch (error) {
      console.error("Error fetching user subscription:", error);
      return null;
    }
  }

  /**
   * Get plan details with caching
   */
  private async getPlanCached(planId: string): Promise<PlanTier | null> {
    if (this.planCache.has(planId)) {
      return this.planCache.get(planId) || null;
    }

    try {
      const { data, error } = await supabase
        .from("base_plan_tiers")
        .select("*")
        .eq("id", planId)
        .single();

      if (error) {
        console.error("Error fetching plan:", error);
        return null;
      }

      const plan = data as PlanTier;
      this.planCache.set(planId, plan);
      return plan;
    } catch (error) {
      console.error("Error fetching plan:", error);
      return null;
    }
  }

  /**
   * Check if a feature exists in plan features
   */
  private hasFeatureInPlan(planFeatures: Record<string, any>, featureName: string): boolean {
    // Check direct feature flag
    if (planFeatures[featureName] === true) {
      return true;
    }

    // Check nested feature object
    if (planFeatures[featureName] && typeof planFeatures[featureName] === 'object') {
      return true;
    }

    // Check for feature in nested objects (e.g., scopingai.enabled)
    for (const [key, value] of Object.entries(planFeatures)) {
      if (typeof value === 'object' && value !== null) {
        if (key === featureName || (value as any)[featureName]) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Get feature limits for a user
   */
  async getFeatureLimits(featureName: string, userId?: string): Promise<Record<string, number>> {
    try {
      if (!userId) {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return {};
        userId = user.id;
      }

      const subscription = await this.getUserSubscriptionCached(userId);
      if (!subscription) return {};

      const plan = await this.getPlanCached(subscription.plan_id);
      if (!plan) return {};

      const planFeatures = plan.features || {};
      const featureConfig = planFeatures[featureName];

      if (typeof featureConfig === 'object' && featureConfig !== null) {
        return featureConfig as Record<string, number>;
      }

      return {};
    } catch (error) {
      console.error(`Error getting feature limits for ${featureName}:`, error);
      return {};
    }
  }

  /**
   * Clear cache for a user
   */
  clearUserCache(userId: string): void {
    const cacheKey = `user_${userId}`;
    this.subscriptionCache.delete(cacheKey);
    this.cacheExpiry.delete(cacheKey);
  }

  /**
   * Clear all caches
   */
  clearAllCaches(): void {
    this.subscriptionCache.clear();
    this.planCache.clear();
    this.cacheExpiry.clear();
  }

  /**
   * Get user's current plan name
   */
  async getUserPlanName(userId?: string): Promise<string> {
    try {
      if (!userId) {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return "No Plan";
        userId = user.id;
      }

      const subscription = await this.getUserSubscriptionCached(userId);
      if (!subscription) return "No Plan";

      const plan = await this.getPlanCached(subscription.plan_id);
      return plan?.name || "Unknown Plan";
    } catch (error) {
      console.error("Error getting user plan name:", error);
      return "Error";
    }
  }

  /**
   * Check if user can access admin features
   */
  async hasAdminAccess(userId?: string): Promise<boolean> {
    return this.hasFeatureAccess("formengine", userId);
  }
}

// Export singleton instance
export const frontendSubscriptionService = new FrontendSubscriptionService();
export default frontendSubscriptionService;

// React hook for feature access checking
export const useFeatureAccess = (featureName: string) => {
  const [hasAccess, setHasAccess] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const checkAccess = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const access = await frontendSubscriptionService.hasFeatureAccess(featureName);
        setHasAccess(access);
      } catch (err) {
        console.error(`Error checking access for ${featureName}:`, err);
        setError(err instanceof Error ? err.message : 'Unknown error');
        setHasAccess(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAccess();
  }, [featureName]);

  return { hasAccess, isLoading, error };
};

// React hook for multiple feature access checking
export const useMultipleFeatureAccess = (featureNames: string[]) => {
  const [accessMap, setAccessMap] = useState<Record<string, boolean>>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const checkMultipleAccess = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const accessPromises = featureNames.map(async (featureName) => {
          const access = await frontendSubscriptionService.hasFeatureAccess(featureName);
          return { featureName, access };
        });

        const results = await Promise.all(accessPromises);
        const newAccessMap: Record<string, boolean> = {};

        results.forEach(({ featureName, access }) => {
          newAccessMap[featureName] = access;
        });

        setAccessMap(newAccessMap);
      } catch (err) {
        console.error('Error checking multiple feature access:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');

        // Set all features to false on error
        const errorAccessMap: Record<string, boolean> = {};
        featureNames.forEach(name => {
          errorAccessMap[name] = false;
        });
        setAccessMap(errorAccessMap);
      } finally {
        setIsLoading(false);
      }
    };

    if (featureNames.length > 0) {
      checkMultipleAccess();
    }
  }, [featureNames.join(',')]); // Use join to create stable dependency

  return { accessMap, isLoading, error };
};

// React hook for user plan information
export const useUserPlan = () => {
  const [planName, setPlanName] = useState<string>('Loading...');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPlanName = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const name = await frontendSubscriptionService.getUserPlanName();
        setPlanName(name);
      } catch (err) {
        console.error('Error fetching user plan:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
        setPlanName('Error');
      } finally {
        setIsLoading(false);
      }
    };

    fetchPlanName();
  }, []);

  return { planName, isLoading, error };
};
